import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { RouteName } from "@superego/routing";
import { valibotSchemas as schemaValibotSchemas } from "@superego/schema";
import { valibotSchemas as backendUtilsValibotSchemas } from "@superego/shared-utils";
import { useEffect, useMemo, useRef } from "react";
import { Form } from "react-aria-components";
import { useForm, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as v from "valibot";
import { useCreateCollection } from "../../../../business-logic/backend/hooks.js";
import forms from "../../../../business-logic/forms/forms.js";
import useNavigationState from "../../../../business-logic/navigation/useNavigationState.js";
import FullPageTabs from "../../../design-system/FullPageTabs/FullPageTabs.js";
import FormStateEffects from "../../../widgets/FormStateEffects/FormStateEffects.js";
import ContentBlockingKeysTab from "./ContentBlockingKeysTab.js";
import ContentSummaryTab from "./ContentSummaryTab.js";
import type CreateCollectionFormValues from "./CreateCollectionFormValues.js";
import GeneralSettingsTab from "./GeneralSettingsTab.js";
import SchemaTab from "./SchemaTab.js";
import TabTitle from "./TabTitle.js";

const defaultSchema = forms.defaults.schema();

export default function CreateCollectionForm() {
  const intl = useIntl();
  const { navigateTo } = useNavigationState();

  const { result, mutate } = useCreateCollection();

  const defaultContentBlockingKeysGetter = useMemo(
    () => forms.defaults.contentBlockingKeysGetter(defaultSchema),
    [],
  );
  const defaultContentSummaryGetter = useMemo(
    () => forms.defaults.contentSummaryGetter(defaultSchema),
    [],
  );
  const { control, handleSubmit, setValue, getValues, formState } =
    useForm<CreateCollectionFormValues>({
      defaultValues: {
        name: "",
        icon: null,
        description: null,
        assistantInstructions: null,
        redirectToCollectionAfterDocumentCreation: false,
        schema: defaultSchema,
        contentBlockingKeysGetter: defaultContentBlockingKeysGetter,
        contentSummaryGetter: defaultContentSummaryGetter,
      },
      mode: "all",
      resolver: standardSchemaResolver(
        v.strictObject({
          name: backendUtilsValibotSchemas.collectionName(),
          icon: v.nullable(backendUtilsValibotSchemas.icon()),
          description: v.nullable(v.string()),
          assistantInstructions: v.nullable(v.string()),
          redirectToCollectionAfterDocumentCreation: v.boolean(),
          schema: schemaValibotSchemas.schema(),
          contentBlockingKeysGetter: v.nullable(
            forms.schemas.typescriptModule(intl),
          ),
          contentSummaryGetter: forms.schemas.typescriptModule(intl),
        }),
      ),
    });

  const onSubmit = async ({
    schema,
    name,
    icon,
    description,
    assistantInstructions,
    redirectToCollectionAfterDocumentCreation,
    contentBlockingKeysGetter,
    contentSummaryGetter,
  }: CreateCollectionFormValues) => {
    const { success, data } = await mutate({
      settings: {
        name,
        icon,
        collectionCategoryId: null,
        defaultCollectionViewAppId: null,
        description,
        assistantInstructions,
        redirectToCollectionAfterDocumentCreation,
      },
      schema,
      versionSettings: {
        contentBlockingKeysGetter,
        contentSummaryGetter,
        defaultDocumentViewUiOptions: null,
      },
    });
    if (success) {
      navigateTo(
        { name: RouteName.Collection, collectionId: data.id },
        { ignoreExitWarning: true },
      );
    }
  };

  const schema = useWatch({ control, name: "schema" });
  const isSchemaValid = !(
    typeof schema === "string" || formState.errors.schema
  );

  // Track the last-applied default source for each dependent field. We use refs
  // instead of formState.defaultValues because resetField (which would update
  // formState.defaultValues) silently does nothing when the target field's
  // controller is not mounted — which is the case here because
  // react-aria-components' TabPanel only renders the active tab's content.
  // setValue, on the other hand, always updates _formValues regardless of
  // whether the field is registered, so we pair it with a ref to track whether
  // the user has customized the source.
  const lastDefaultContentBlockingKeysGetterSourceRef = useRef(
    defaultContentBlockingKeysGetter?.source,
  );
  const lastDefaultContentSummaryGetterSourceRef = useRef(
    defaultContentSummaryGetter.source,
  );

  // When schema changes, if it's valid:
  // - Update contentSummaryGetter and contentBlockingKeysGetter, if they are
  //   still the default ones, and in any case require recompilation.
  useEffect(() => {
    if (!isSchemaValid) {
      return;
    }

    const currentContentBlockingKeysGetterSource = getValues(
      "contentBlockingKeysGetter.source",
    );
    if (
      currentContentBlockingKeysGetterSource ===
      lastDefaultContentBlockingKeysGetterSourceRef.current
    ) {
      const newDefault = forms.defaults.contentBlockingKeysGetter(schema);
      setValue("contentBlockingKeysGetter", newDefault);
      lastDefaultContentBlockingKeysGetterSourceRef.current = newDefault.source;
    } else {
      setValue(
        "contentBlockingKeysGetter.compiled",
        forms.constants.COMPILATION_REQUIRED,
      );
    }

    const currentContentSummaryGetterSource = getValues(
      "contentSummaryGetter.source",
    );
    if (
      currentContentSummaryGetterSource ===
      lastDefaultContentSummaryGetterSourceRef.current
    ) {
      const newDefault = forms.defaults.contentSummaryGetter(schema);
      setValue("contentSummaryGetter", newDefault);
      lastDefaultContentSummaryGetterSourceRef.current = newDefault.source;
    } else {
      setValue(
        "contentSummaryGetter.compiled",
        forms.constants.COMPILATION_REQUIRED,
      );
    }
  }, [schema, setValue, getValues, isSchemaValid]);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FormStateEffects control={control} triggerExitWarningWhenDirty={true} />
      <FullPageTabs
        tabs={[
          {
            title: (
              <TabTitle
                hasErrors={
                  !!(
                    formState.errors.icon ||
                    formState.errors.name ||
                    formState.errors.description ||
                    formState.errors.assistantInstructions
                  )
                }
              >
                <FormattedMessage defaultMessage="1. General settings" />
              </TabTitle>
            ),
            panel: <GeneralSettingsTab control={control} />,
          },
          {
            title: (
              <TabTitle hasErrors={!!formState.errors.schema}>
                <FormattedMessage defaultMessage="2. Schema" />
              </TabTitle>
            ),
            panel: <SchemaTab control={control} />,
          },
          {
            title: (
              <TabTitle
                hasErrors={!!formState.errors.contentBlockingKeysGetter}
              >
                <FormattedMessage defaultMessage="3. Deduplication" />
              </TabTitle>
            ),
            panel: <ContentBlockingKeysTab control={control} schema={schema} />,
            isDisabled: !isSchemaValid,
          },
          {
            title: (
              <TabTitle hasErrors={!!formState.errors.contentSummaryGetter}>
                <FormattedMessage defaultMessage="4. Content summary" />
              </TabTitle>
            ),
            panel: (
              <ContentSummaryTab
                control={control}
                schema={schema}
                result={result}
              />
            ),
            isDisabled: !isSchemaValid,
          },
        ]}
      />
    </Form>
  );
}
